// Contact Form Conversion Tracking Service
// Handles Google Analytics 4 events for contact form submissions with proper consent checks

// Module-level state for managing gtag availability polling
let isPollingGtag = false;        // Prevents multiple intervals
let gtagReadyCallbacks = [];      // Queue of pending callbacks
let gtagReady = false;            // Caches gtag availability status

// Utility function to check if tracking is allowed with current consent
const canTrack = (type = 'analytics') => {
  if (typeof window === 'undefined') return false;

  try {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) return false;

    const preferences = JSON.parse(consent);

    switch (type) {
      case 'analytics':
        return preferences.analytics === true;
      case 'marketing':
      case 'ads':
        return preferences.marketing === true;
      case 'functionality':
        return preferences.functionality === true;
      default:
        return false;
    }
  } catch (error) {
    console.error('Error checking consent for tracking:', error);
    return false;
  }
};

/**
 * Centralized gtag availability manager. Ensures only one polling interval
 * runs regardless of how many times tracking is called before gtag loads.
 *
 * @param {Function} callback - Function to execute when gtag becomes available
 */
const waitForGtag = (callback) => {
  // If gtag already available, execute immediately
  if (gtagReady || (typeof window !== 'undefined' && window.gtag)) {
    gtagReady = true;
    callback();
    return;
  }

  // Add to callback queue
  gtagReadyCallbacks.push(callback);

  // Start polling only if not already polling
  if (!isPollingGtag) {
    isPollingGtag = true;
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max (50 × 100ms)

    const pollInterval = setInterval(() => {
      attempts++;

      // Check if gtag is now available
      if (typeof window !== 'undefined' && window.gtag) {
        gtagReady = true;
        clearInterval(pollInterval);
        isPollingGtag = false;

        // Execute all queued callbacks
        const callbacks = [...gtagReadyCallbacks];
        gtagReadyCallbacks = [];
        callbacks.forEach(cb => cb());
      }
      // Timeout reached - give up
      else if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        isPollingGtag = false;

        // Clear queue without executing (gtag never loaded)
        gtagReadyCallbacks = [];
        console.warn('ConversionTracking: gtag failed to load after 5 seconds');
      }
    }, 100);
  }
};

// Enhanced tracking wrapper that waits for consent and gtag availability
const trackWithConsent = (trackingFunction, consentType = 'analytics') => {
  return (...args) => {
    if (typeof window === 'undefined') return;

    const executeTracking = () => {
      if (window.gtag && canTrack(consentType)) {
        trackingFunction(...args);
      }
    };

    // Check if gtag is immediately available
    if (window.gtag) {
      gtagReady = true;
      executeTracking();
    } else {
      // Use centralized waiting mechanism
      waitForGtag(executeTracking);
    }
  };
};

export const ConversionTracking = {
  // Contact form submission tracking
  trackContactForm: trackWithConsent((formData = {}) => {
    window.gtag('event', 'generate_lead', {
      event_category: 'Contact',
      event_label: 'Contact Form Submission',
      value: 1,
      currency: 'EUR',
      ...formData
    });

    // Track as conversion for Google Ads
    window.gtag('event', 'conversion', {
      send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL', // Replace with your actual conversion ID
      value: 1,
      currency: 'EUR'
    });
  }, 'analytics')
};

export default ConversionTracking;
